import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Pull-to-refresh hook for mobile touch devices.
 *
 * Only active when window.scrollY === 0 and the user touches.
 * Tracks vertical delta, exposes `pullDistance` (0-MAX_PULL) and `refreshing`.
 * Fires onRefresh once the user releases past `threshold`.
 */
export default function usePullToRefresh({
  onRefresh,
  threshold = 80,
  maxPull = 140,
  enabled = true,
} = {}) {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const touchStartY = useRef(null);
  const pulling = useRef(false);

  const handleTouchStart = useCallback((e) => {
    if (!enabled || refreshing) return;
    if (window.scrollY > 0) return;
    touchStartY.current = e.touches[0].clientY;
    pulling.current = false;
  }, [enabled, refreshing]);

  const handleTouchMove = useCallback((e) => {
    if (!enabled || refreshing || touchStartY.current === null) return;
    const delta = e.touches[0].clientY - touchStartY.current;
    if (delta <= 0) {
      pulling.current = false;
      setPullDistance(0);
      return;
    }
    if (window.scrollY > 0) {
      pulling.current = false;
      setPullDistance(0);
      return;
    }
    pulling.current = true;
    // Rubber-band damping after threshold
    const damped = delta < threshold ? delta : threshold + (delta - threshold) * 0.4;
    const next = Math.min(maxPull, damped);
    setPullDistance(next);
  }, [enabled, refreshing, threshold, maxPull]);

  const handleTouchEnd = useCallback(async () => {
    if (!enabled || refreshing) {
      touchStartY.current = null;
      pulling.current = false;
      return;
    }
    const wasPulling = pulling.current;
    const dist = pullDistance;
    touchStartY.current = null;
    pulling.current = false;
    if (wasPulling && dist >= threshold) {
      setRefreshing(true);
      setPullDistance(threshold);
      try {
        await Promise.resolve(onRefresh?.());
      } finally {
        // Hold for a beat so the user sees the spinner; then release.
        setTimeout(() => {
          setRefreshing(false);
          setPullDistance(0);
        }, 450);
      }
    } else {
      setPullDistance(0);
    }
  }, [enabled, refreshing, pullDistance, threshold, onRefresh]);

  useEffect(() => {
    if (!enabled) return;
    const opts = { passive: true };
    window.addEventListener('touchstart', handleTouchStart, opts);
    window.addEventListener('touchmove', handleTouchMove, opts);
    window.addEventListener('touchend', handleTouchEnd, opts);
    window.addEventListener('touchcancel', handleTouchEnd, opts);
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [enabled, handleTouchStart, handleTouchMove, handleTouchEnd]);

  return {
    pullDistance,
    refreshing,
    progress: Math.min(1, pullDistance / threshold),
    threshold,
  };
}
